let CacheMaster = null;

// Ensure Aerospike is only used on the server
if (typeof window === "undefined") {
  const Aerospike = require("aerospike");
  const zlib = require("zlib");

  // Use global variables to persist across module reloads in development
  global._aerospikeClient = global._aerospikeClient || null;
  global._aerospikeIsConnecting = global._aerospikeIsConnecting || false;
  global._aerospikeConnectionCount = global._aerospikeConnectionCount || 0;

  let client = global._aerospikeClient;
  let isConnecting = global._aerospikeIsConnecting;
  let connectionCount = global._aerospikeConnectionCount;
  // Constants for namespace and set
  const NAMESPACE = process?.env?.NEXT_PUBLIC_ENV === "development" ? "flights" : "seo"; // Replace with your desired namespace
  const SET = "flights_seo";
  const AEROSPIKETIMEOUT = 30000; // 30 seconds

  async function createAerospikeClient() {
    const config =
      process?.env?.NEXT_PUBLIC_ENV === "development"
        ? {
            hosts: [{ addr: "10.213.57.169", port: 3000 }],
            policies: {
              read: new Aerospike.ReadPolicy({ timeout: AEROSPIKETIMEOUT }),
              write: new Aerospike.WritePolicy({ timeout: AEROSPIKETIMEOUT }),
            },
          }
        : {
            hosts: [
              { addr: "aerospike-common-pwa-1.mmt.mmt", port: 3000 },
              { addr: "aerospike-common-pwa-2.mmt.mmt", port: 3000 },
              { addr: "aerospike-common-pwa-3.mmt.mmt", port: 3000 },
            ], // Adjust your Aerospike host
            policies: {
              read: new Aerospike.ReadPolicy({ timeout: AEROSPIKETIMEOUT }),
              write: new Aerospike.WritePolicy({ timeout: AEROSPIKETIMEOUT }),
            },
          };

    try {
      client = await Aerospike.connect(config);
      connectionCount++;

      // Update global variables
      global._aerospikeClient = client;
      global._aerospikeConnectionCount = connectionCount;

      console.log(
        `Connected to Aerospike via CacheMaster singleton (connection #${connectionCount})`,
      );

      // Add connection event listeners for monitoring
      client.on("disconnect", () => {
        console.log("Aerospike connection disconnected");
        client = null;
        global._aerospikeClient = null;
      });

      client.on("error", (err) => {
        console.error("Aerospike connection error:", err);
      });

      return client;
    } catch (err) {
      console.error("Aerospike Connection Error:", err);
      client = null;
      global._aerospikeClient = null;
      throw err;
    }
  }

  async function getAerospikeClient() {
    // If already connected and client is healthy, return existing connection
    if (client) {
      try {
        // Check if client is still connected
        if (client.isConnected && client.isConnected()) {
          return client;
        }
        // If connection check fails, try a simple operation as fallback
        await client.info("build", client.config.hosts[0]);
        return client;
      } catch (err) {
        console.log("Existing Aerospike connection lost, reconnecting...", err.message);
        client = null;
        global._aerospikeClient = null;
      }
    }

    // Prevent multiple concurrent connection attempts
    if (isConnecting) {
      // Wait for the ongoing connection attempt
      while (isConnecting) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      // After waiting, check if connection was established
      if (client) {
        return client;
      }
    }

    // Create new connection
    if (!client) {
      isConnecting = true;
      global._aerospikeIsConnecting = true;
      try {
        client = await createAerospikeClient();
      } finally {
        isConnecting = false;
        global._aerospikeIsConnecting = false;
      }
    }
    return client;
  }

  async function truncate() {
    const aerospikeClient = await getAerospikeClient();
    aerospikeClient.truncate(null, NAMESPACE, SET, 0);
  }

  async function setCache(key, resp, time) {
    const aerospikeClient = await getAerospikeClient();
    const aerospikeKey = new Aerospike.Key(NAMESPACE, SET, key);
    try {
      const record = { SEO_RESPONSE: zlib.gzipSync(JSON.stringify(resp)), version: 2 }; // Version 2 is compressed data
      await aerospikeClient.put(aerospikeKey, record, { ttl: time });
      console.log(`Data cached under key: ${key} with TTL: ${time}s`);
    } catch (err) {
      console.error("Error setting cache in Aerospike:", err);
    }
  }
  async function getCache(key) {
    const aerospikeClient = await getAerospikeClient();
    const aerospikeKey = new Aerospike.Key(NAMESPACE, SET, key);
    try {
      const record = await aerospikeClient.get(aerospikeKey);
      const value = record.bins.SEO_RESPONSE;
      let result = JSON.parse(zlib.gunzipSync(value).toString());
      return result;
    } catch (err) {
      if (err.code === Aerospike.status.AEROSPIKE_ERR_RECORD_NOT_FOUND) {
        return null;
      }
      return null;
    }
  }

  async function deleteKey(key) {
    const aerospikeClient = await getAerospikeClient();
    const aerospikeKey = new Aerospike.Key(NAMESPACE, SET, key);
    try {
      await aerospikeClient.remove(aerospikeKey);
      console.log(`Deleted cache under key: ${key}`);
    } catch (err) {
      console.error("Error deleting cache from Aerospike:", err);
      throw err;
    }
  }

  // Health check function
  async function healthCheck() {
    try {
      const aerospikeClient = await getAerospikeClient();
      if (!aerospikeClient) {
        return { status: "unhealthy", message: "Aerospike client not available" };
      }

      // Perform info command to verify connection
      const buildInfo = await aerospikeClient.info("build", aerospikeClient.config.hosts[0]);

      // Get cluster info
      const clusterInfo = await aerospikeClient.info(
        "cluster-name",
        aerospikeClient.config.hosts[0],
      );

      return {
        status: "healthy",
        message: "Aerospike connection is active",
        details: {
          namespace: NAMESPACE,
          set: SET,
          totalConnectionsCreated: connectionCount,
          singletonWorking:
            connectionCount === 1 ? "YES" : `NO - ${connectionCount} connections created`,
          buildInfo: buildInfo,
          clusterName: clusterInfo || "unknown",
          isConnected: aerospikeClient.isConnected ? aerospikeClient.isConnected() : "unknown",
        },
      };
    } catch (err) {
      console.error("Aerospike health check failed:", err);
      return {
        status: "unhealthy",
        message: err.message,
        error: err.name,
      };
    }
  }

  // Get connection statistics
  function getConnectionStats() {
    return {
      isConnected: !!(client && (client.isConnected ? client.isConnected() : true)),
      totalConnectionsCreated: connectionCount,
      isConnecting: isConnecting,
      namespace: NAMESPACE,
      set: SET,
    };
  }

  CacheMaster = {
    getCache,
    setCache,
    getAerospikeClient,
    truncate,
    deleteKey,
    healthCheck,
    getConnectionStats,
  };
} else {
  // No-op functions for client-side to avoid runtime errors
  CacheMaster = {
    getCache: async () => null,
    setCache: async () => null,
    getAerospikeClient: async () => null,
    truncate: async () => null,
    deleteKey: async () => null,
    healthCheck: async () => ({
      status: "client-side",
      message: "Aerospike not available on client",
    }),
    getConnectionStats: () => ({
      isConnected: false,
      totalConnectionsCreated: 0,
      isConnecting: false,
      namespace: null,
      set: null,
    }),
  };
}

export default CacheMaster;
